import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Vault, MagnifyingGlass, Trash, PencilSimple, LinkSimple, User, FileText, Sparkle, CaretDown, CaretRight, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canManagePlanner } from '@/auth/AuthContext';
import { plannerApi } from './plannerApi';
import { OldProjectFields, formToBody, fieldsToForm, EMPTY_FORM } from './components/CreateOldProjectDialog';

const NONE = '__none__';

function OldProjectCard({ rec, onOpen }) {
  return (
    <button type="button" onClick={onOpen} data-testid={`oldproj-card-${rec.id}`}
      className="text-left rounded-xl border border-zinc-200 bg-white shadow-sm p-4 hover:border-violet-300 hover:shadow transition-all space-y-1.5 w-full">
      <div className="flex items-start gap-2">
        <Vault size={16} weight="duotone" className="text-violet-600 mt-0.5 shrink-0" />
        <span className="text-sm font-bold text-zinc-800 leading-snug">{rec.name}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-zinc-500">
        {(rec.date_start || rec.date_end) && <span>{rec.date_start || '?'}〜{rec.date_end || ''}</span>}
        {rec.department_text && <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px] px-1.5 py-0">{rec.department_text}</Badge>}
        {(rec.people || []).length > 0 && (
          <span className="inline-flex items-center gap-1"><User size={11} /> {rec.people.slice(0, 3).join('、')}{rec.people.length > 3 ? ` 他${rec.people.length - 3}名` : ''}</span>
        )}
      </div>
      {rec.summary && <p className="text-xs text-zinc-600 leading-relaxed line-clamp-3">{rec.summary}</p>}
      {(rec.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {rec.tags.slice(0, 6).map((k) => <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 border border-violet-100 text-violet-700">{k}</span>)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[10px] text-zinc-400 pt-0.5">
        {rec.source_article_title && <span className="inline-flex items-center gap-1 min-w-0"><FileText size={11} className="shrink-0" /> <span className="truncate">{rec.source_article_title}</span></span>}
        {rec.linked_project_name && <span className="inline-flex items-center gap-1 text-indigo-600"><LinkSimple size={11} /> {rec.linked_project_name}</span>}
      </div>
    </button>
  );
}

function InfoRow({ label, children }) {
  if (!children) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold text-zinc-400">{label}</p>
      <div className="text-sm text-zinc-700 mt-0.5">{children}</div>
    </div>
  );
}

export default function ArchivedOldProjects() {
  const { t } = useT();
  const { user } = useAuth();
  const manager = canManagePlanner(user);
  const [searchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [linkSel, setLinkSel] = useState(NONE);
  const [showExcerpt, setShowExcerpt] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    plannerApi.oldProjects(q ? { q } : {}).then((d) => setRecords(d.records || [])).catch(() => {});
  }, [q]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  useEffect(() => { if (manager) plannerApi.projects({ include_archived: true }).then(setProjects).catch(() => {}); }, [manager]);

  const openDetail = useCallback((id) => {
    plannerApi.oldProject(id).then((d) => {
      setDetail(d); setEditing(false); setShowExcerpt(false);
      setLinkSel(d.linked_project_id || NONE);
    }).catch((e) => toast.error(e?.response?.data?.detail || t('planner.error')));
  }, [t]);

  useEffect(() => {
    const oid = searchParams.get('open');
    if (oid) openDetail(oid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => { setForm(fieldsToForm(detail)); setEditing(true); };
  const saveEdit = async () => {
    if (!(form.name || '').trim()) { toast.error(`${t('planner.oldproj.name')}は必須です`); return; }
    setBusy(true);
    try {
      const d = await plannerApi.updateOldProject(detail.id, formToBody(form));
      setDetail({ ...detail, ...d }); setEditing(false); load();
      toast.success(t('planner.saved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
    setBusy(false);
  };
  const saveLink = async (val) => {
    setLinkSel(val);
    try {
      const d = await plannerApi.updateOldProject(detail.id, { linked_project_id: val === NONE ? null : val });
      setDetail({ ...detail, ...d }); load();
      toast.success(t('planner.saved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };
  const doDelete = async () => {
    if (!window.confirm(t('planner.oldproj.deleteConfirm'))) return;
    try {
      await plannerApi.deleteOldProject(detail.id);
      toast.success(t('planner.oldproj.deleted'));
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  return (
    <div className="space-y-5" data-testid="oldproj-vault-page">
      <div>
        <div className="flex items-center gap-2">
          <Vault size={22} weight="duotone" className="text-violet-600" />
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">{t('planner.oldproj.title')}</h1>
          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] px-1.5 py-0">{t('planner.oldproj.count', { n: records.length })}</Badge>
        </div>
        <p className="text-xs text-zinc-500 mt-1">{t('planner.oldproj.subtitle')}</p>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('planner.oldproj.search')} className="pl-9 h-9" data-testid="oldproj-search-input" />
      </div>

      {records.length === 0 ? (
        <p className="text-sm text-zinc-400 text-center py-14" data-testid="oldproj-empty">{t('planner.oldproj.empty')}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="oldproj-grid">
          {records.map((rec) => <OldProjectCard key={rec.id} rec={rec} onOpen={() => openDetail(rec.id)} />)}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="oldproj-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
                  <Vault size={18} weight="duotone" className="text-violet-600 shrink-0" />
                  <span data-testid="oldproj-detail-name">{detail.name}</span>
                  {detail.extraction_method && (
                    <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] px-1.5 py-0">
                      {detail.extraction_method === 'ai' && <Sparkle size={10} className="mr-0.5" />}{t(`planner.oldproj.method.${detail.extraction_method}`)}
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              {editing ? (
                <OldProjectFields form={form} setForm={setForm} t={t} />
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <InfoRow label={`${t('planner.oldproj.dateStart')}〜${t('planner.oldproj.dateEnd')}`}>
                      {(detail.date_start || detail.date_end) ? `${detail.date_start || '?'}〜${detail.date_end || ''}` : null}
                    </InfoRow>
                    <InfoRow label={t('planner.oldproj.department')}>{detail.department_text}</InfoRow>
                    <InfoRow label={t('planner.oldproj.people')}>{(detail.people || []).join('、')}</InfoRow>
                    <InfoRow label={t('planner.oldproj.linkedProject')}>{detail.linked_project_name}</InfoRow>
                  </div>
                  {detail.summary && (
                    <div>
                      <p className="text-[11px] font-semibold text-zinc-400">{t('planner.oldproj.summary')}</p>
                      <p className="text-sm text-zinc-700 mt-0.5 leading-relaxed whitespace-pre-wrap" data-testid="oldproj-detail-summary">{detail.summary}</p>
                    </div>
                  )}
                  {(detail.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {detail.tags.map((k) => <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 border border-violet-100 text-violet-700">{k}</span>)}
                    </div>
                  )}
                  <div className="rounded-lg border border-amber-200/70 bg-amber-50/40 p-3 space-y-1.5" data-testid="oldproj-source-block">
                    <p className="text-[11px] font-bold text-amber-700 inline-flex items-center gap-1"><FileText size={12} /> {t('planner.oldproj.sourceArticle')}</p>
                    <p className="text-xs text-zinc-600">
                      {detail.source_article_title}
                      {detail.source_article_id ? `（#${detail.source_article_id}）` : ''}
                      {detail.source_file_name ? ` — ${detail.source_file_name}` : ''}
                    </p>
                    {detail.source_url && (
                      <a href={detail.source_url} target="_blank" rel="noreferrer" data-testid="oldproj-source-url"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700">
                        <ArrowSquareOut size={11} /> {detail.source_url}
                      </a>
                    )}
                    {detail.raw_excerpt && (
                      <div>
                        <button type="button" onClick={() => setShowExcerpt((v) => !v)} data-testid="oldproj-detail-excerpt-toggle"
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700">
                          {showExcerpt ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />} {t('planner.oldproj.sourceExcerpt')}
                        </button>
                        {showExcerpt && <pre className="mt-1 p-2 rounded bg-white border border-zinc-200 text-[11px] text-zinc-600 whitespace-pre-wrap max-h-60 overflow-y-auto">{detail.raw_excerpt}</pre>}
                      </div>
                    )}
                  </div>
                  {manager && (
                    <div>
                      <label className="text-xs font-semibold text-zinc-600">{t('planner.oldproj.linkToProject')}</label>
                      <Select value={linkSel} onValueChange={saveLink}>
                        <SelectTrigger className="h-9 mt-1" data-testid="oldproj-link-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>{t('planner.oldproj.noLink')}</SelectItem>
                          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="gap-2 sm:justify-between">
                {manager ? (
                  <Button variant="outline" onClick={doDelete} data-testid="oldproj-delete-btn"
                    className="text-rose-600 border-rose-200 hover:bg-rose-50">
                    <Trash size={14} className="mr-1" /> {t('planner.oldproj.delete')}
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  {editing ? (
                    <>
                      <Button variant="outline" onClick={() => setEditing(false)}>{t('planner.cancel')}</Button>
                      <Button onClick={saveEdit} disabled={busy} data-testid="oldproj-edit-save-btn" className="bg-teal-700 hover:bg-teal-800">{t('planner.save')}</Button>
                    </>
                  ) : manager && (
                    <Button variant="outline" onClick={startEdit} data-testid="oldproj-edit-btn">
                      <PencilSimple size={14} className="mr-1" /> {t('planner.oldproj.edit')}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
